import { supabase } from "./supabase";
import type { AppNotification, Category, CategoryAttribute, Chat, DeletedChatMessage, DeletedChatSummary, Listing, ListingChatSummary, ListingClosureFeedback, ListingClosureReason, ListingDraft, ListingDraftPayload, ListingPhoto, ListingStatus, Message, ModerationDecision, Profile, RecommendedPriceResponse, SuggestionEventType, TitleSuggestionResponse, TitleSuggestionRule } from "../types/domain";
import { findProfanityMatches, profanityMessage } from "./profanity";

const listingSelect = "*, profile:profiles!listings_user_id_fkey(id,display_name,avatar_url,rating,reviews_count,phone,is_blocked), photos:listing_photos(*), category:categories!listings_category_id_fkey(id,name,slug,parent_id), subcategory:categories!listings_subcategory_id_fkey(id,name,slug,parent_id), leaf_category:categories!listings_leaf_category_id_fkey(id,name,slug,parent_id)";
const chatSelect = "*, listing:listings!chats_listing_id_fkey(id,title,price,currency,status,photos:listing_photos(*)), buyer:profiles!chats_buyer_id_fkey(id,display_name,avatar_url,city,rating,reviews_count), seller:profiles!chats_seller_id_fkey(id,display_name,avatar_url,city,rating,reviews_count)";
const notificationSelect = `*, listing:listings(${listingSelect})`;
const aiImageLimit = 3;
const listingVisitorStorageKey = "premamarket_listing_visitor_id";
let volatileListingVisitorId: string | null = null;

function listingVisitorId(): string {
  if (volatileListingVisitorId) return volatileListingVisitorId;
  try {
    const stored = window.localStorage.getItem(listingVisitorStorageKey)?.trim();
    if (stored && stored.length >= 16 && stored.length <= 128) {
      volatileListingVisitorId = stored;
      return stored;
    }
    const created = crypto.randomUUID();
    window.localStorage.setItem(listingVisitorStorageKey, created);
    volatileListingVisitorId = created;
    return created;
  } catch {
    volatileListingVisitorId = crypto.randomUUID();
    return volatileListingVisitorId;
  }
}

function storagePathsForPhoto(photo: Pick<ListingPhoto, "storage_path">): string[] {
  if (!photo.storage_path) return [];
  const slashIndex = photo.storage_path.lastIndexOf("/");
  const folder = slashIndex >= 0 ? photo.storage_path.slice(0, slashIndex + 1) : "";
  const fileName = slashIndex >= 0 ? photo.storage_path.slice(slashIndex + 1) : photo.storage_path;
  return [photo.storage_path, `${folder}thumb-${fileName}`];
}

export async function getCurrentProfile(): Promise<Profile | null> {
  const { data: auth } = await supabase.auth.getUser();
  if (!auth.user) return null;
  const { data, error } = await supabase.from("profiles").select("*").eq("id", auth.user.id).maybeSingle();
  if (error) throw error;
  return data as Profile | null;
}

export async function updateCurrentProfile(input: Partial<Pick<Profile, "avatar_url" | "notify_email_messages">>): Promise<Profile> {
  const { data: auth } = await supabase.auth.getUser();
  if (!auth.user) throw new Error("Нужен вход.");
  const { data, error } = await supabase
    .from("profiles")
    .update(input)
    .eq("id", auth.user.id)
    .select("*")
    .single();
  if (error) throw error;
  return data as Profile;
}

export async function getPublicProfile(userId: string): Promise<Profile | null> {
  const { data, error } = await supabase.from("profiles").select("*").eq("id", userId).maybeSingle();
  if (error) throw error;
  return data as Profile | null;
}

export async function authEmailExists(email: string): Promise<boolean | null> {
  const { data, error } = await supabase.rpc("auth_email_exists", { p_email: email.trim() });
  if (error) {
    console.warn("Не удалось проверить email", error);
    return null;
  }
  return Boolean(data);
}

export async function listCategories(): Promise<Category[]> {
  const { data, error } = await supabase
    .from("categories")
    .select("*")
    .order("sort_order", { ascending: true })
    .order("name", { ascending: true });
  if (error) throw error;
  return (data ?? []) as Category[];
}

export async function listCategoryAttributes(categoryId: number | null): Promise<CategoryAttribute[]> {
  if (!categoryId) return [];
  const { data, error } = await supabase
    .from("category_attributes")
    .select("*")
    .eq("category_id", categoryId)
    .order("sort_order", { ascending: true })
    .order("label", { ascending: true });
  if (error) {
    console.warn("Не удалось загрузить характеристики категории", error);
    return [];
  }
  return (data ?? []) as CategoryAttribute[];
}

export async function listTitleSuggestionRules(categoryIds: number[]): Promise<TitleSuggestionRule[]> {
  if (!categoryIds.length) return [];
  const { data, error } = await supabase
    .from("title_suggestion_rules")
    .select("*")
    .in("category_id", categoryIds)
    .eq("is_active", true)
    .order("priority", { ascending: false })
    .order("trigger", { ascending: true });
  if (error) {
    console.warn("Не удалось загрузить подсказки названия", error);
    return [];
  }
  return (data ?? []) as TitleSuggestionRule[];
}

export async function getTitleSuggestions(input: { title: string; categoryId?: number | null; imageDataUrls?: string[]; descriptionOnly?: boolean; listingAttributes?: Record<string, string | number | boolean | null>; condition?: string | null }): Promise<TitleSuggestionResponse | null> {
  if (input.title.trim().length < 3) return null;
  const { data, error } = await supabase.functions.invoke("title-suggestions", {
    body: {
      title: input.title,
      categoryId: input.categoryId ?? null,
      imageDataUrls: input.imageDataUrls?.slice(0, aiImageLimit) ?? [],
      descriptionOnly: Boolean(input.descriptionOnly),
      listingAttributes: input.listingAttributes ?? {},
      condition: input.condition ?? null
    }
  });
  if (error) {
    console.warn("Не удалось получить AI-подсказки названия", error);
    return null;
  }
  return data as TitleSuggestionResponse;
}

export async function getRecommendedPrice(input: {
  title: string;
  description: string;
  listingAttributes?: Record<string, string | number | boolean | null>;
  categoryId: number;
  condition: string | null;
  conditionApplicable: boolean;
  listingId?: number | null;
}): Promise<RecommendedPriceResponse> {
  const { data, error } = await supabase.functions.invoke("recommended-price", { body: input });
  if (error) throw error;
  return data as RecommendedPriceResponse;
}

export type ContentModerationVerdict = {
  isClean: boolean;
  reasons: string[];
  matches?: string[];
  flaggedImages?: number[];
};

export async function moderateContent(input: {
  mode: "text" | "image" | "listing";
  text?: string;
  title?: string;
  description?: string;
  categoryPath?: string[];
  material?: string;
  imageDataUrls?: string[];
}): Promise<ContentModerationVerdict | null> {
  const { data, error } = await supabase.functions.invoke("content-moderation", {
    body: {
      mode: input.mode,
      text: input.text ?? "",
      title: input.title ?? "",
      description: input.description ?? "",
      categoryPath: input.categoryPath ?? [],
      material: input.material ?? "",
      imageDataUrls: input.imageDataUrls ?? []
    }
  });
  if (error) {
    console.warn("Не удалось выполнить модерацию", error);
    return null;
  }
  return data as ContentModerationVerdict;
}

export async function recordSuggestionEvent(input: {
  impressionId?: string | null;
  eventType: SuggestionEventType;
  title: string;
  categoryId?: number | null;
  itemValue?: string | null;
  listingId?: number | null;
  metadata?: Record<string, unknown>;
}): Promise<void> {
  const { error } = await supabase.functions.invoke("suggestion-event", {
    body: {
      impressionId: input.impressionId ?? null,
      eventType: input.eventType,
      title: input.title,
      categoryId: input.categoryId ?? null,
      itemValue: input.itemValue ?? null,
      listingId: input.listingId ?? null,
      metadata: input.metadata ?? {}
    }
  });
  if (error) console.warn("Не удалось записать событие подсказок", error);
}

export async function notifyPendingModeration(listingId: number): Promise<void> {
  const { error } = await supabase.functions.invoke("moderation-notify", {
    body: { listingId }
  });
  if (error) throw error;
}

export async function listNotifications(isAdmin: boolean): Promise<AppNotification[]> {
  const { data: auth } = await supabase.auth.getUser();
  if (!auth.user) throw new Error("Нужен вход.");
  let query = supabase
    .from("notifications")
    .select(notificationSelect)
    .order("created_at", { ascending: false });
  if (isAdmin) query = query.eq("recipient_role", "admin");
  else query = query.eq("recipient_user_id", auth.user.id);
  const { data, error } = await query.limit(300);
  if (error) throw error;
  return ((data ?? []) as unknown as Array<AppNotification & { listing: Listing | Listing[] | null }>).map((item) => ({
    ...item,
    listing: Array.isArray(item.listing) ? item.listing[0] ?? null : item.listing ?? null
  }));
}

export async function getUnreadNotificationCount(isAdmin: boolean): Promise<number> {
  const { data: auth } = await supabase.auth.getUser();
  if (!auth.user) return 0;
  let query = supabase
    .from("notifications")
    .select("id", { count: "exact", head: true })
    .is("read_at", null);
  if (isAdmin) query = query.eq("recipient_role", "admin");
  else query = query.eq("recipient_user_id", auth.user.id);
  const { count, error } = await query;
  if (error) throw error;
  return count ?? 0;
}

export async function markNotificationRead(notificationId: number): Promise<void> {
  const { data: auth } = await supabase.auth.getUser();
  if (!auth.user) throw new Error("Нужен вход.");
  const { error } = await supabase
    .from("notifications")
    .update({ read_at: new Date().toISOString() })
    .eq("id", notificationId);
  if (error) throw error;
}

export async function listAdminModerationListings(): Promise<Listing[]> {
  const { data, error } = await supabase
    .from("listings")
    .select(listingSelect)
    .or("status.in.(pending_manual_review,rejected,blocked),and(status.eq.active,moderation_decision.eq.approved)")
    .order("updated_at", { ascending: false });
  if (error) throw error;
  return (data ?? []) as Listing[];
}

export async function listAdminListingClosures(): Promise<ListingClosureFeedback[]> {
  const { data, error } = await supabase
    .from("listing_closure_feedback")
    .select("*")
    .is("restored_at", null)
    .order("closed_at", { ascending: false });
  if (error) throw error;
  return (data ?? []) as ListingClosureFeedback[];
}

export async function moderateListing(listingId: number, decision: ModerationDecision, reason?: string): Promise<Listing> {
  const { data, error } = await supabase.rpc("admin_moderate_listing", {
    p_listing_id: listingId,
    p_decision: decision,
    p_reason: reason ?? null
  });
  if (error) throw error;
  return data as Listing;
}

export async function sendListingToModeration(listingId: number, reason?: string): Promise<Listing> {
  const { data, error } = await supabase.rpc("admin_send_listing_to_moderation", {
    p_listing_id: listingId,
    p_reason: reason ?? null
  });
  if (error) throw error;
  return data as Listing;
}

function formatPostgrestList(values: string[]) {
  return values.map((value) => `"${value.replace(/\\/g, "\\\\").replace(/"/g, '\\"')}"`).join(",");
}

export async function listListings(params: {
  query?: string;
  categoryId?: number | null;
  countryCode?: string | null;
  cities?: string[];
  regions?: string[];
  condition?: string | null;
  priceMin?: number | null;
  priceMax?: number | null;
  sort?: "newest" | "price_asc" | "price_desc";
  attributeFilters?: Record<string, string | number | boolean | null>;
  includeInactive?: boolean;
} = {}): Promise<Listing[]> {
  if (params.query?.trim()) {
    const { data, error } = await supabase
      .rpc("search_listings", {
        p_query: params.query.trim(),
        p_category_id: params.categoryId ?? null,
        p_country_code: params.countryCode ?? null,
        p_cities: params.cities?.length ? params.cities : null,
        p_regions: params.regions?.length ? params.regions : null,
        p_condition: params.condition ?? null,
        p_price_min: params.priceMin ?? null,
        p_price_max: params.priceMax ?? null,
        p_attribute_filters: params.attributeFilters ?? {},
        p_include_inactive: params.includeInactive ?? false,
        p_sort: params.sort ?? "newest"
      })
      .select(listingSelect);

    if (!error) return (data ?? []) as Listing[];
    console.warn("search_listings RPC failed, falling back to basic listing search.", error);
  }

  let query = supabase
    .from("listings")
    .select(listingSelect);

  if (!params.includeInactive) query = query.eq("status", "active");
  if (params.query?.trim()) {
    const term = params.query.trim().replace(/%/g, "\\%");
    query = query.or(`title.ilike.%${term}%,description.ilike.%${term}%`);
  }
  if (params.categoryId) query = query.contains("category_path", [String(params.categoryId)]);
  if (params.countryCode) query = query.eq("country_code", params.countryCode);
  if (params.cities?.length && params.regions?.length) {
    query = query.or(`city.in.(${formatPostgrestList(params.cities)}),region.in.(${formatPostgrestList(params.regions)})`);
  } else if (params.cities?.length) {
    query = query.in("city", params.cities);
  } else if (params.regions?.length) {
    query = query.in("region", params.regions);
  }
  if (params.condition) query = query.eq("item_condition", params.condition);
  if (params.priceMin != null) query = query.gte("price", params.priceMin);
  if (params.priceMax != null) query = query.lte("price", params.priceMax);
  Object.entries(params.attributeFilters ?? {}).forEach(([key, value]) => {
    if (value === null || value === "" || value === undefined) return;
    query = query.contains("attributes", { [key]: value });
  });

  if (params.sort === "price_asc") query = query.order("price", { ascending: true });
  else if (params.sort === "price_desc") query = query.order("price", { ascending: false });
  else query = query.order("created_at", { ascending: false });

  const { data, error } = await query;
  if (error) throw error;
  return (data ?? []) as Listing[];
}

export async function getListing(id: number): Promise<Listing | null> {
  const { data, error } = await supabase
    .from("listings")
    .select(listingSelect)
    .eq("id", id)
    .maybeSingle();
  if (error) throw error;
  return data as Listing | null;
}

export async function recordListingView(listingId: number): Promise<boolean> {
  const { data, error } = await supabase.rpc("record_listing_view", {
    p_listing_id: listingId,
    p_visitor_token: listingVisitorId()
  });
  if (error) throw error;
  return Boolean(data);
}

export async function listMyFavoriteListingIds(listingIds?: number[]): Promise<number[]> {
  const { data: auth } = await supabase.auth.getUser();
  if (!auth.user) return [];
  if (listingIds && !listingIds.length) return [];
  let query = supabase
    .from("favorites")
    .select("listing_id")
    .eq("user_id", auth.user.id);
  if (listingIds) query = query.in("listing_id", listingIds);
  const { data, error } = await query;
  if (error) throw error;
  return (data ?? []).map((item) => Number(item.listing_id));
}

export async function isListingFavorited(listingId: number): Promise<boolean> {
  const { data: auth } = await supabase.auth.getUser();
  if (!auth.user) return false;
  const { data, error } = await supabase
    .from("favorites")
    .select("listing_id")
    .eq("user_id", auth.user.id)
    .eq("listing_id", listingId)
    .maybeSingle();
  if (error) throw error;
  return Boolean(data);
}

export async function listMyFavoriteListings(): Promise<Listing[]> {
  const { data: auth } = await supabase.auth.getUser();
  if (!auth.user) throw new Error("Нужен вход.");
  const { data, error } = await supabase
    .from("favorites")
    .select(`created_at, listing:listings!favorites_listing_id_fkey(${listingSelect})`)
    .eq("user_id", auth.user.id)
    .order("created_at", { ascending: false });
  if (error) throw error;
  return ((data ?? []) as unknown as Array<{ listing: Listing | Listing[] | null }>)
    .flatMap((item) => Array.isArray(item.listing) ? item.listing : item.listing ? [item.listing] : []);
}

export async function addFavorite(listingId: number): Promise<void> {
  const { data: auth } = await supabase.auth.getUser();
  if (!auth.user) throw new Error("Нужен вход.");
  const { error } = await supabase
    .from("favorites")
    .upsert({ user_id: auth.user.id, listing_id: listingId }, { onConflict: "user_id,listing_id", ignoreDuplicates: true });
  if (error) throw error;
}

export async function removeFavorite(listingId: number): Promise<void> {
  const { data: auth } = await supabase.auth.getUser();
  if (!auth.user) throw new Error("Нужен вход.");
  const { error } = await supabase
    .from("favorites")
    .delete()
    .eq("user_id", auth.user.id)
    .eq("listing_id", listingId);
  if (error) throw error;
}

export async function listMyListings(): Promise<Listing[]> {
  const { data: auth } = await supabase.auth.getUser();
  if (!auth.user) throw new Error("Нужен вход.");
  const { data, error } = await supabase
    .from("listings")
    .select(listingSelect)
    .eq("user_id", auth.user.id)
    .order("updated_at", { ascending: false });
  if (error) throw error;
  return (data ?? []) as Listing[];
}

export async function listProfileListings(userId: string): Promise<Listing[]> {
  const { data, error } = await supabase
    .from("listings")
    .select(listingSelect)
    .eq("user_id", userId)
    .eq("status", "active")
    .order("created_at", { ascending: false });
  if (error) throw error;
  return (data ?? []) as Listing[];
}

export async function setListingStatus(listingId: number, status: Extract<ListingStatus, "active" | "archived">): Promise<Listing> {
  const { data: auth } = await supabase.auth.getUser();
  if (!auth.user) throw new Error("Нужен вход.");
  const { data, error } = await supabase
    .from("listings")
    .update({ status, updated_at: new Date().toISOString() })
    .eq("id", listingId)
    .eq("user_id", auth.user.id)
    .select(listingSelect)
    .single();
  if (error) throw error;
  return data as Listing;
}

export async function closeListingWithFeedback(input: {
  listingId: number;
  reason: ListingClosureReason;
  salePrice?: number | null;
  salePlatform?: string | null;
  details?: string | null;
}): Promise<Listing> {
  const { data, error } = await supabase.rpc("close_listing_with_feedback", {
    p_listing_id: input.listingId,
    p_reason: input.reason,
    p_sale_price: input.salePrice ?? null,
    p_sale_platform: input.salePlatform?.trim() || null,
    p_details: input.details?.trim() || null
  });
  if (error) throw error;
  return data as Listing;
}

export async function deleteListing(listingId: number): Promise<void> {
  const { data: auth } = await supabase.auth.getUser();
  if (!auth.user) throw new Error("Нужен вход.");
  const { data: photos, error: photoError } = await supabase
    .from("listing_photos")
    .select("*")
    .eq("listing_id", listingId);
  if (photoError) throw photoError;

  const { error } = await supabase
    .from("listings")
    .delete()
    .eq("id", listingId)
    .eq("user_id", auth.user.id);
  if (error) throw error;

  const paths = (photos ?? []).flatMap((photo) => storagePathsForPhoto(photo as ListingPhoto));
  if (paths.length) {
    const { error: storageError } = await supabase.storage.from("listing-photos").remove(paths);
    if (storageError) console.warn("Не удалось удалить файлы объявления", storageError);
  }
}

export async function createListing(input: Partial<Listing>): Promise<Listing> {
  const { data: auth } = await supabase.auth.getUser();
  if (!auth.user) throw new Error("Нужен вход.");
  const { data, error } = await supabase
    .from("listings")
    .insert({ ...input, user_id: auth.user.id, status: input.status ?? "active" })
    .select("*")
    .single();
  if (error) throw error;
  return data as Listing;
}

export async function updateListing(listingId: number, input: Partial<Listing>): Promise<Listing> {
  const { data: auth } = await supabase.auth.getUser();
  if (!auth.user) throw new Error("Нужен вход.");
  const safeInput: Partial<Listing> = { ...input };
  delete safeInput.id;
  delete safeInput.user_id;
  const { data, error } = await supabase
    .from("listings")
    .update({ ...safeInput, updated_at: new Date().toISOString() })
    .eq("id", listingId)
    .eq("user_id", auth.user.id)
    .select("*")
    .single();
  if (error) throw error;
  return data as Listing;
}

export async function addListingPhotos(listingId: number, photos: Array<Omit<ListingPhoto, "id" | "listing_id">>) {
  if (!photos.length) return;
  const { error } = await supabase.from("listing_photos").insert(photos.map((photo) => ({ ...photo, listing_id: listingId })));
  if (error) throw error;
}

export async function deleteListingPhoto(photo: ListingPhoto): Promise<void> {
  const paths = storagePathsForPhoto(photo);

  const { error } = await supabase
    .from("listing_photos")
    .delete()
    .eq("id", photo.id)
    .eq("listing_id", photo.listing_id);
  if (error) throw error;

  if (paths.length) {
    const { error: storageError } = await supabase.storage.from("listing-photos").remove(paths);
    if (storageError) console.warn("Не удалось удалить файлы фото", storageError);
  }
}

export async function listListingDrafts(): Promise<ListingDraft[]> {
  const { data: auth } = await supabase.auth.getUser();
  if (!auth.user) throw new Error("Нужен вход.");
  const { data, error } = await supabase
    .from("listing_drafts")
    .select("*")
    .eq("user_id", auth.user.id)
    .order("updated_at", { ascending: false });
  if (error) throw error;
  return (data ?? []) as ListingDraft[];
}

export async function getListingDraft(draftId: number): Promise<ListingDraft | null> {
  const { data: auth } = await supabase.auth.getUser();
  if (!auth.user) throw new Error("Нужен вход.");
  const { data, error } = await supabase
    .from("listing_drafts")
    .select("*")
    .eq("id", draftId)
    .eq("user_id", auth.user.id)
    .maybeSingle();
  if (error) throw error;
  return data as ListingDraft | null;
}

export async function upsertListingDraft(input: {
  id?: number | null;
  listingId?: number | null;
  currentStep: number;
  payload: ListingDraftPayload;
}): Promise<ListingDraft> {
  const { data: auth } = await supabase.auth.getUser();
  if (!auth.user) throw new Error("Нужен вход.");
  const values = {
    user_id: auth.user.id,
    listing_id: input.listingId ?? null,
    current_step: input.currentStep,
    payload: input.payload,
    updated_at: new Date().toISOString()
  };
  const query = input.id
    ? supabase.from("listing_drafts").update(values).eq("id", input.id).eq("user_id", auth.user.id)
    : supabase.from("listing_drafts").insert(values);
  const { data, error } = await query.select("*").single();
  if (error) throw error;
  return data as ListingDraft;
}

export async function deleteListingDraft(draftId: number): Promise<void> {
  const { data: auth } = await supabase.auth.getUser();
  if (!auth.user) throw new Error("Нужен вход.");
  const { data: draft, error: draftError } = await supabase
    .from("listing_drafts")
    .select("payload")
    .eq("id", draftId)
    .eq("user_id", auth.user.id)
    .maybeSingle();
  if (draftError) throw draftError;
  const { error } = await supabase
    .from("listing_drafts")
    .delete()
    .eq("id", draftId)
    .eq("user_id", auth.user.id);
  if (error) throw error;
  const draftPhotos = (draft?.payload as ListingDraftPayload | null)?.photos ?? [];
  const paths = draftPhotos.flatMap((photo) => storagePathsForPhoto(photo));
  if (paths.length) {
    const { error: storageError } = await supabase.storage.from("listing-photos").remove(paths);
    if (storageError) console.warn("Не удалось удалить фото черновика", storageError);
  }
}

export async function createOrGetChat(listing: Listing): Promise<Chat> {
  const { data: auth } = await supabase.auth.getUser();
  if (!auth.user) throw new Error("Нужен вход.");
  if (auth.user.id === listing.user_id) throw new Error("Это ваше объявление.");
  const { data, error } = await supabase
    .rpc("create_or_get_chat", {
      p_listing_id: listing.id,
      p_buyer_id: auth.user.id,
      p_seller_id: listing.user_id
    })
    .single();
  if (error) throw error;
  return data as Chat;
}

async function attachChatMetadata<T extends Chat>(chats: T[]): Promise<Array<T & { unread_count: number; last_message: Message | null }>> {
  const { data: auth } = await supabase.auth.getUser();
  if (!auth.user) throw new Error("Нужен вход.");
  const ids = chats.map((chat) => chat.id);
  if (!ids.length) return chats.map((chat) => ({ ...chat, unread_count: 0, last_message: null }));

  const { data: messages, error } = await supabase
    .from("messages")
    .select("*")
    .in("chat_id", ids)
    .order("created_at", { ascending: false });
  if (error) {
    console.warn("Не удалось загрузить последние сообщения", error);
    return chats.map((chat) => ({ ...chat, unread_count: 0, last_message: null }));
  }

  const latestByChat = new Map<number, Message>();
  const unreadByChat = new Map<number, number>();
  (messages ?? []).forEach((message) => {
    const typed = message as Message;
    if (!latestByChat.has(typed.chat_id)) latestByChat.set(typed.chat_id, typed);
    if (typed.sender_id !== auth.user!.id && !typed.read_at) {
      unreadByChat.set(typed.chat_id, (unreadByChat.get(typed.chat_id) ?? 0) + 1);
    }
  });

  return chats.map((chat) => ({
    ...chat,
    unread_count: unreadByChat.get(chat.id) ?? 0,
    last_message: latestByChat.get(chat.id) ?? null
  }));
}

export async function listMyChats(): Promise<ListingChatSummary[]> {
  const { data: auth } = await supabase.auth.getUser();
  if (!auth.user) throw new Error("Нужен вход.");
  const { data, error } = await supabase
    .from("chats")
    .select(chatSelect)
    .or(`buyer_id.eq.${auth.user.id},seller_id.eq.${auth.user.id}`)
    .is("deleted_at", null)
    .order("updated_at", { ascending: false });
  if (error) throw error;
  return attachChatMetadata((data ?? []) as ListingChatSummary[]);
}

export async function getChat(chatId: number): Promise<Chat | null> {
  const { data: auth } = await supabase.auth.getUser();
  if (!auth.user) throw new Error("Нужен вход.");
  const { data, error } = await supabase
    .from("chats")
    .select(chatSelect)
    .eq("id", chatId)
    .is("deleted_at", null)
    .or(`buyer_id.eq.${auth.user.id},seller_id.eq.${auth.user.id}`)
    .maybeSingle();
  if (error) throw error;
  return data as Chat | null;
}

export async function listMyListingChats(): Promise<ListingChatSummary[]> {
  const { data: auth } = await supabase.auth.getUser();
  if (!auth.user) throw new Error("Нужен вход.");
  const { data, error } = await supabase
    .from("chats")
    .select(chatSelect)
    .eq("seller_id", auth.user.id)
    .is("deleted_at", null)
    .order("updated_at", { ascending: false });
  if (error) throw error;

  return attachChatMetadata((data ?? []) as ListingChatSummary[]);
}

export async function deleteMyChats(chatIds: number[]): Promise<void> {
  if (!chatIds.length) return;
  const { error } = await supabase.rpc("delete_my_chats", { p_chat_ids: chatIds });
  if (error) throw error;
}

export async function listDeletedChatArchive(): Promise<DeletedChatSummary[]> {
  const { data, error } = await supabase.rpc("admin_list_deleted_chats");
  if (error) throw error;
  return (data ?? []) as DeletedChatSummary[];
}

export async function listDeletedChatMessages(chatId: number): Promise<DeletedChatMessage[]> {
  const { data, error } = await supabase.rpc("admin_list_deleted_chat_messages", { p_chat_id: chatId });
  if (error) throw error;
  return (data ?? []) as DeletedChatMessage[];
}

export async function getUnreadMessageCount(): Promise<number> {
  const { data: auth } = await supabase.auth.getUser();
  if (!auth.user) return 0;
  const { data: chats, error: chatsError } = await supabase
    .from("chats")
    .select("id")
    .is("deleted_at", null)
    .or(`buyer_id.eq.${auth.user.id},seller_id.eq.${auth.user.id}`);
  if (chatsError) {
    console.warn("Не удалось загрузить чаты", chatsError);
    return 0;
  }
  const ids = (chats ?? []).map((chat) => chat.id);
  if (!ids.length) return 0;
  const { count, error } = await supabase
    .from("messages")
    .select("id", { count: "exact", head: true })
    .in("chat_id", ids)
    .neq("sender_id", auth.user.id)
    .is("read_at", null);
  if (error) {
    console.warn("Не удалось загрузить непрочитанные сообщения", error);
    return 0;
  }
  return count ?? 0;
}

export async function touchCurrentUserLastSeen(): Promise<void> {
  const { data: auth } = await supabase.auth.getUser();
  if (!auth.user) return;
  const { error } = await supabase.rpc("touch_last_seen", { p_user_id: auth.user.id });
  if (error) console.warn("Не удалось обновить активность", error);
}

export async function listMessages(chatId: number): Promise<Message[]> {
  const { data, error } = await supabase
    .from("messages")
    .select("*")
    .eq("chat_id", chatId)
    .order("created_at", { ascending: true });
  if (error) throw error;
  return (data ?? []) as Message[];
}

export async function sendMessage(chatId: number, body: string): Promise<Message> {
  const { data: auth } = await supabase.auth.getUser();
  if (!auth.user) throw new Error("Нужен вход.");
  const matches = findProfanityMatches(body);
  if (matches.length) throw new Error(profanityMessage(matches));
  const { data, error } = await supabase
    .from("messages")
    .insert({ chat_id: chatId, sender_id: auth.user.id, body })
    .select("*")
    .single();
  if (error) throw error;
  return data as Message;
}
